import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X, CheckCircle2 } from 'lucide-react';
import { applyUpdate } from '../utils/pwaUpdate';
import {
  AUTO_UPDATE_AT_KEY, AUTO_UPDATE_TRIES_KEY, STUCK_TRIES, UPDATED_NOTICE_KEY, WAIT_POLL_MS,
  decideUpdate, isUserBusy, shouldShowManualBar, type UpdateAction,
} from '../utils/autoUpdateModel';
import { probeBusy, watchTyping } from '../utils/pwaAutoUpdate';

/** How often a long-open tab asks the server whether a new version exists. */
const UPDATE_CHECK_MS = 5 * 60_000;

function readNumber(key: string): number | null {
  try {
    const v = sessionStorage.getItem(key);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

/**
 * D-341 (3): a new version refreshes the site by itself - no "approve new version" click.
 * Never in the middle of work (open dialog, typing, a form with unsaved changes): then it waits for the next page
 * transition. The running timer lives on the server and drafts are in the forms' own guards, so nothing is lost.
 * After the refresh a small bar says "עודכן לגרסה חדשה". If the refresh does not take (a stuck service worker), the
 * old manual prompt shows instead of looping.
 */
export default function UpdatePrompt() {
  const { t } = useTranslation();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [updatedBar, setUpdatedBar] = useState(false);
  const [manual, setManual] = useState(false);
  const needRefreshRef = useRef(false);
  const waitingSinceRef = useRef<number | null>(null);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // A tab left open all day still finds out about a new version.
      window.setInterval(() => { void registration.update().catch(() => undefined); }, UPDATE_CHECK_MS);
    },
    onRegisterError(error) {
      console.warn('SW registration failed, clearing stale caches:', error);
      if ('caches' in window) {
        caches.keys().then(names =>
          names.forEach(name => caches.delete(name))
        );
      }
    },
  });

  needRefreshRef.current = needRefresh;

  // After an automatic refresh: the small "updated" bar, a few seconds.
  useEffect(() => {
    watchTyping();
    // A healthy minute after load (no pending new version) means the last refresh took: forget the failed tries.
    const healthy = window.setTimeout(() => { if (!needRefreshRef.current) { try { sessionStorage.removeItem(AUTO_UPDATE_TRIES_KEY); } catch { /* ignore */ } } }, 60_000);
    let flagged = false;
    try {
      flagged = sessionStorage.getItem(UPDATED_NOTICE_KEY) === '1';
      if (flagged) sessionStorage.removeItem(UPDATED_NOTICE_KEY);
    } catch { /* storage blocked: no bar */ }
    if (!flagged) return () => window.clearTimeout(healthy);
    setUpdatedBar(true);
    const timer = window.setTimeout(() => setUpdatedBar(false), 6000);
    return () => { window.clearTimeout(timer); window.clearTimeout(healthy); };
  }, []);

  const runAutoUpdate = async () => {
    try {
      sessionStorage.setItem(UPDATED_NOTICE_KEY, '1');
      sessionStorage.setItem(AUTO_UPDATE_AT_KEY, String(Date.now()));
    } catch { /* still refresh */ }
    waitingSinceRef.current = null;
    try { sessionStorage.setItem(AUTO_UPDATE_TRIES_KEY, String((readNumber(AUTO_UPDATE_TRIES_KEY) ?? 0) + 1)); } catch { /* ignore */ }
    await applyUpdate(updateServiceWorker);
  };

  // A new version was found: refresh now if idle. If busy (or in cooldown) stay silent, re-check every 15s and on every
  // page transition, and refresh the moment the user is not busy. The manual bar is only the last resort.
  useEffect(() => {
    if (!needRefresh) { waitingSinceRef.current = null; return; }
    let timer: number | undefined;
    const tick = () => {
      const last = readNumber(AUTO_UPDATE_AT_KEY);
      const sinceLast = last === null ? null : Date.now() - last;
      const action: UpdateAction = decideUpdate({ needRefresh: true, busy: isUserBusy(probeBusy()), msSinceLastAuto: sinceLast });
      const failedTries = readNumber(AUTO_UPDATE_TRIES_KEY) ?? 0;
      if (action === 'apply') {
        // Earlier automatic refreshes did not clear the new-version state: a stuck service worker. Stop and offer the manual bar.
        if (failedTries >= STUCK_TRIES) { setManual(true); return; }
        void runAutoUpdate();
        return;
      }
      if (waitingSinceRef.current === null) waitingSinceRef.current = Date.now();
      if (shouldShowManualBar({ waitingMs: Date.now() - waitingSinceRef.current, failedTries: 0 })) setManual(true);
      // Busy or in cooldown: stay silent, look again soon (a cooldown that ends sooner is picked up at the next tick).
      timer = window.setTimeout(tick, WAIT_POLL_MS);
    };
    tick();
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefresh, location.pathname]);

  const handleUpdate = async () => {
    if (busy) return;
    setBusy(true);
    await applyUpdate(updateServiceWorker);
    // applyUpdate triggers a hard reload, so we don't really get here. Just in
    // case something goes wrong, clear the busy flag.
    setBusy(false);
  };

  return (
    <>
      {updatedBar && (
        <div
          role="status"
          data-testid="updated-bar"
          className="fixed bottom-4 start-4 end-4 md:end-auto md:start-4 z-50 flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-4 py-2 text-sm text-success-700 shadow-elevated"
        >
          <CheckCircle2 size={16} aria-hidden="true" />
          {t('pwa.updatedBar')}
        </div>
      )}
      {needRefresh && manual && (
        <div className="fixed bottom-4 start-4 end-4 md:end-auto md:start-4 md:w-80 z-50 bg-surface-0 border border-surface-300/60 shadow-elevated rounded-lg p-4 flex items-center gap-3">
          <RefreshCw size={20} className={`text-accent-text flex-shrink-0 ${busy ? 'animate-spin' : ''}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-surface-800">{t('pwa.updateAvailable')}</p>
          </div>
          <button
            onClick={() => void handleUpdate()}
            disabled={busy}
            className="btn btn-primary text-xs py-1.5 px-3 flex-shrink-0 disabled:opacity-60"
          >
            {busy ? t('pwa.updating') : t('pwa.update')}
          </button>
          <button
            onClick={() => setNeedRefresh(false)}
            className="text-surface-500 hover:text-surface-600 flex-shrink-0"
            aria-label={t('pwa.dismiss')}
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
}
